/**
 * Tags — Phase D's foundation, designed to serve THREE masters at once
 * (DELIVERIT_STRATEGY.md §4):
 * - project tags — Jack's boss/PM reporting ("Ajax progress").
 * - role tags — Jill's hats (CEO / MD / QM). Her verdict: "Essential."
 * - deliverable tags — DeliverIt's Core Deliverables (5–7 items), so the
 *   Clarify phase of the methodology is born into the data model.
 *
 * A task may carry MULTIPLE tags (a task can belong to a project AND a
 * role). Tags are colored (Jill: "colour code by category as well as
 * urgency") and sync with per-item editedAt merge discipline.
 */

import { compareTasks, ZONES, type Task } from '../types';
import { taskProgress } from './progress';
import { formatFullDate } from './dates';

export type TagKind = 'project' | 'role' | 'deliverable';

export interface Tag {
  id: string;
  name: string;
  kind: TagKind;
  /** Hex color — drives the chip/dot everywhere the tag appears. */
  color: string;
  createdAt: number;
  editedAt?: number;
  /** Weekly Insights (13 Oct 2026): ★ "this is really my job". Toggling
   *  bumps editedAt so the per-tag merge carries it. Absent = not core
   *  (frozen Notebook legends predate it). */
  core?: boolean;
}

export const TAG_KINDS: { id: TagKind; label: string }[] = [
  { id: 'project', label: 'Project' },
  { id: 'role', label: 'Role' },
  { id: 'deliverable', label: 'Deliverable' },
];

/**
 * Distinguishable AT CHIP SIZE — the test that matters. The first cut had
 * emerald+teal and blue+indigo pairs that read identical in a 10px dot
 * (tester-reported); this set keeps every pair visually apart in light and
 * dark themes. Red is deliberately absent: red means urgency in ZoneIn.
 */
export const TAG_COLORS = [
  '#2563eb', // blue
  '#16a34a', // green
  '#ea580c', // orange
  '#7c3aed', // violet
  '#db2777', // pink
  '#0891b2', // cyan
  '#ca8a04', // mustard
  '#64748b', // slate
];

/** Round-robin over the palette so early tags are maximally distinct. */
export function nextTagColor(existing: { color: string }[]): string {
  const counts = TAG_COLORS.map((c) => existing.filter((t) => t.color === c).length);
  const min = Math.min(...counts);
  return TAG_COLORS[counts.indexOf(min)];
}

const norm = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();

/** Case-insensitive lookup — "Ajax" and "ajax" are one tag, not two. */
export function findTagByName(tags: Tag[], name: string): Tag | undefined {
  const n = norm(name);
  return tags.find((t) => norm(t.name) === n);
}

/**
 * Scanned outlines produce "Header: item" titles — the header IS the
 * project. Suggest it as a tag so grouping costs one tap, not typing.
 * Null when there's no prefix or it's too long to be a name.
 */
export function suggestTagName(taskText: string): string | null {
  const m = /^([^:]{2,32}):\s+\S/.exec(taskText.trim());
  if (!m) return null;
  const name = m[1].replace(/\s+/g, ' ').trim();
  // A prefix that's mostly a sentence isn't a project name.
  if (name.split(' ').length > 4) return null;
  return name;
}

/**
 * Ongoing recognition: when a new task's "Header: item" prefix matches an
 * EXISTING tag, assign it automatically — next week's scan of the same
 * notebook groups itself. Deliberately never auto-CREATES tags: OCR noise
 * must not silently spawn junk tags; creation stays a one-tap suggestion.
 */
export function autoTagFor(text: string, tags: Tag[]): Tag | undefined {
  const name = suggestTagName(text);
  return name ? findTagByName(tags, name) : undefined;
}

export type SortMode = 'priority' | 'date' | 'tag';

/**
 * Sort within a zone.
 * - 'priority' means PRIORITY: ranked by the AI's combined urgency +
 *   importance score (dated items rise via the deadline boost; quick-adds
 *   sit mid-pack at 50/50), done last, creation order as the stable
 *   tiebreaker so every device agrees.
 * - 'date' puts timestamped deadlines first (soonest up), then free-text
 *   dates, then undated.
 * - 'tag' groups by first tag name (untagged last).
 */
export function sortTasks(
  tasks: Task[],
  mode: SortMode,
  tagsById: Record<string, Tag>,
): Task[] {
  const arr = [...tasks];
  if (mode === 'date') {
    // Bucket = the task's LOCAL calendar day (rough dates, then undated, last).
    // Within a bucket: the user's hand order (same-day ordering, 15 Oct 2026),
    // then time of day, then canonical. With no hand order set this is
    // exactly the old order (day, then timestamp). MUST match
    // engine/manualOrder.ts orderGroupKey / compareManual.
    const bucket = (t: Task) => {
      if (t.deadlineAt === undefined) return t.deadlineHint ? Number.MAX_SAFE_INTEGER - 1 : Number.MAX_SAFE_INTEGER;
      const d = new Date(t.deadlineAt);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };
    return arr.sort((a, b) => {
      if (a.done !== b.done) return Number(a.done) - Number(b.done);
      const ra = bucket(a);
      const rb = bucket(b);
      if (ra !== rb) return ra - rb;
      const ma = a.manualRank ?? Number.MAX_SAFE_INTEGER;
      const mb = b.manualRank ?? Number.MAX_SAFE_INTEGER;
      if (ma !== mb) return ma - mb;
      const ta = a.deadlineAt ?? 0;
      const tb = b.deadlineAt ?? 0;
      if (ta !== tb) return ta - tb;
      return compareTasks(a, b);
    });
  }
  if (mode === 'tag') {
    const keyOf = (t: Task) => {
      const first = (t.tags ?? []).map((id) => tagsById[id]).find(Boolean);
      return first ? first.name.toLowerCase() : '￿'; // untagged last
    };
    return arr.sort((a, b) => {
      if (a.done !== b.done) return Number(a.done) - Number(b.done);
      const ka = keyOf(a);
      const kb = keyOf(b);
      if (ka !== kb) return ka < kb ? -1 : 1;
      const ma = a.manualRank ?? Number.MAX_SAFE_INTEGER;
      const mb = b.manualRank ?? Number.MAX_SAFE_INTEGER;
      if (ma !== mb) return ma - mb;
      return compareTasks(a, b);
    });
  }
  return arr.sort((a, b) => {
    if (a.done !== b.done) return Number(a.done) - Number(b.done);
    const sa = a.urgency + a.importance;
    const sb = b.urgency + b.importance;
    if (sa !== sb) return sb - sa;
    const ma = a.manualRank ?? Number.MAX_SAFE_INTEGER;
    const mb = b.manualRank ?? Number.MAX_SAFE_INTEGER;
    if (ma !== mb) return ma - mb;
    return compareTasks(a, b);
  });
}

/** Coverage numbers for one tag — Jill's "which categories are covered
 *  well and where I should put some time". */
export interface TagStats {
  open: number;
  done: number;
  /** Mean task progress across the tag's tasks, 0..1. */
  progress: number;
}

export function tagStats(tasks: Task[], tagId: string): TagStats {
  const mine = tasks.filter((t) => (t.tags ?? []).includes(tagId));
  const done = mine.filter((t) => t.done).length;
  const progress =
    mine.length === 0 ? 0 : mine.reduce((acc, t) => acc + taskProgress(t), 0) / mine.length;
  return { open: mine.length - done, done, progress };
}

/**
 * Per-tag status report — Jack's Q9 verbatim use case: "If my boss asks
 * me to quickly update him on Ajax's progress, I can efficiently update
 * him from project groupings."
 *
 * Enterprise register (Chad, 31 Jul): this text lands in a CEO's inbox —
 * structured like a real status memo (SUMMARY / COMPLETED / IN PROGRESS /
 * NOT STARTED), plain professional language, overdue items surfaced, no
 * emoji, no app jargon. Plain text so it pastes cleanly into email,
 * Slack, Teams, or Word. The UI presents it in an EDITABLE preview before
 * sharing — the generated text is a first draft, never forced verbatim.
 */
export function tagReport(
  tag: Tag,
  tasks: Task[],
  weekLabelText: string,
  now: number = Date.now(),
): string {
  const mine = tasks.filter((t) => (t.tags ?? []).includes(tag.id));
  const done = mine.filter((t) => t.done).sort(compareTasks);
  const open = mine.filter((t) => !t.done).sort(compareTasks);
  const inProgress = open.filter((t) => taskProgress(t) > 0);
  const notStarted = open.filter((t) => taskProgress(t) === 0);
  const pct = Math.round(tagStats(tasks, tag.id).progress * 100);

  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const isOverdue = (t: Task) =>
    typeof t.deadlineAt === 'number' && t.deadlineAt < startOfToday.getTime();
  const overdueCount = open.filter(isOverdue).length;

  const lines: string[] = [];
  lines.push(`${tag.name.toUpperCase()} — STATUS REPORT`);
  lines.push(`${formatFullDate(new Date(now))} · ${weekLabelText}`);
  lines.push('');

  if (mine.length === 0) {
    lines.push('No items are currently assigned.');
    return lines.join('\n');
  }

  lines.push('SUMMARY');
  const counts: string[] = [];
  counts.push(`${done.length} completed`);
  counts.push(`${inProgress.length} in progress`);
  if (notStarted.length > 0) counts.push(`${notStarted.length} not started`);
  lines.push(
    `${mine.length} item${mine.length === 1 ? '' : 's'}: ${counts.join(', ')} — ${pct}% complete overall.`,
  );
  if (overdueCount > 0) {
    lines.push(
      `${overdueCount} item${overdueCount === 1 ? ' is' : 's are'} past target date — flagged below.`,
    );
  }

  const openLine = (t: Task) => {
    const p = Math.round(taskProgress(t) * 100);
    const meta: string[] = [];
    meta.push(p > 0 ? `${p}% complete` : 'not started');
    if (t.deadlineHint) meta.push(`target ${t.deadlineHint}${isOverdue(t) ? ' (PAST DUE)' : ''}`);
    else if (isOverdue(t)) meta.push('PAST DUE');
    const out = `• ${t.text} — ${meta.join(' · ')}`;
    const sub = t.note?.trim() ? `\n  Status note: ${t.note.trim()}` : '';
    return out + sub;
  };

  if (done.length > 0) {
    lines.push('');
    lines.push('COMPLETED');
    for (const t of done) lines.push(`• ${t.text}`);
  }
  // Open items broken down BY PRIORITY ZONE, each under its zone's own
  // description (Chad, 31 Jul) — the reader sees not just what is open,
  // but how urgent and important each item is.
  if (open.length > 0) {
    lines.push('');
    lines.push('OPEN ITEMS — BY PRIORITY ZONE');
    for (const z of ZONES) {
      const inZone = open.filter((t) => t.zone === z.id);
      if (inZone.length === 0) continue;
      lines.push('');
      lines.push(`${z.title} (${z.subtitle})`);
      for (const t of inZone) lines.push(openLine(t));
    }
  }
  return lines.join('\n');
}
